import React, { useEffect, useState, useCallback } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import { getAnonymousUsers } from "../../services/posthogGeo";
import api from "../../services/api";
import "../Users/Users.scss";

// Admin "Anonymous Users" tab. People who opened the app but never created an
// account. They have no Mongo user doc, so the normal Users tab can't show
// them — they exist only as PostHog persons. Identity tell: signed-in users
// are identify()'d to their Firebase UID; anonymous devices keep PostHog's
// UUID distinct_id. See netlify/functions/posthog-geo.js (?anonymous=1).
// NOTE: PostHog was added recently, so this reflects sessions since then, not
// lifetime installs. It's a floor + a directional read, not a full census.
const fmtMiami = (d) => {
  if (!d) return "—";
  try {
    return (
      new Date(d).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }) + " ET"
    );
  } catch {
    return "—";
  }
};

// "America/Chicago" -> "Chicago" (rough area hint when geoip only resolves to country).
const tzArea = (tz) => (tz ? tz.split("/").pop().replace(/_/g, " ") : null);

const geoLabelOf = (u) => {
  const parts = [u.city, u.region].filter(Boolean);
  if (parts.length) return parts.join(", ");
  // Geoip only got the country (or nothing). Use the timezone as a coarse
  // hint so the row isn't a useless bare "United States".
  const area = tzArea(u.timezone);
  if (u.country && area) return `${u.country} · ~${area}`;
  if (area) return `~${area}`;
  return u.country || "—";
};

const WINDOWS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

const AnonymousUsers = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  // Push-reachability + captured GPS per device, keyed by distinct id, from the
  // backend UnregisteredUser collection. Populated after the PostHog rows load.
  const [reach, setReach] = useState({});
  const [reachStats, setReachStats] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setErr(false);
    setReach({});
    setReachStats(null);
    const res = await getAnonymousUsers(d);
    if (!res || res.error) {
      setErr(true);
      setData(null);
      setLoading(false);
      return;
    }
    setData(res);
    setLoading(false);

    // Enrich with push-reachability + captured location. Best-effort: the tab
    // still renders fully if this fails.
    const ids = (res.users || []).map((u) => u.device).filter(Boolean);
    try {
      const { data: enr } = await api.post("/users/admin/unregistered-lookup", { ids });
      setReach(enr?.data || {});
      setReachStats(enr?.stats || null);
    } catch (e) {
      /* leave push/location columns empty */
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [load, days]);

  const users = data?.users || [];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <h1 style={{ margin: 0, color: "#fff" }}>Anonymous Users</h1>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.55)", fontSize: 13, maxWidth: 820, lineHeight: 1.6 }}>
              People who opened the app but never created an account. They have no user record in the database (that's why
              they don't appear under Users), so this is sourced from analytics — each row is one anonymous device.
              PostHog was added recently, so this covers sessions since then, not lifetime installs: read it as a floor and
              a directional signal, not a full census.
            </p>
          </div>

          {/* Window selector */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                style={{
                  background: days === w.days ? "rgba(79,48,234,0.9)" : "rgba(255,255,255,0.05)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 999,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {w.label}
              </button>
            ))}
          </div>

          {/* Summary */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <Stat label={`Anonymous (${days}d)`} value={loading ? "…" : (data?.total ?? 0)} />
            <Stat label="Active last 7 days" value={loading ? "…" : (data?.last7 ?? 0)} />
            <Stat label="Push-reachable" value={loading ? "…" : (reachStats ? reachStats.totalReachable : "—")} />
            <Stat label="With GPS location" value={loading ? "…" : (reachStats ? reachStats.withLocation : "—")} muted />
            <Stat label="Shown below" value={loading ? "…" : users.length} muted />
          </div>

          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              overflowX: "auto",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 760 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>Device</th>
                  <th style={th}>Location</th>
                  <th style={th}>Push</th>
                  <th style={th}>First seen</th>
                  <th style={th}>Last seen</th>
                  <th style={{ ...th, textAlign: "right" }}>Events</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Could not load anonymous-user data.
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No anonymous users in this window.
                    </td>
                  </tr>
                ) : (
                  users.map((u, i) => {
                    const r = reach[u.device] || {};
                    const hasGps = typeof r.latitude === "number" && typeof r.longitude === "number";
                    return (
                    <tr key={u.device || i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ ...td, fontFamily: "monospace", fontSize: 12, opacity: 0.75 }}>
                        {u.device ? `${u.device.slice(0, 13)}…` : "—"}
                      </td>
                      <td style={td}>
                        {hasGps ? (
                          <>
                            <a
                              href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "#8b7bff", fontWeight: 600 }}
                            >
                              {geoLabelOf(u)}
                            </a>
                            <div style={{ fontSize: 11, color: "rgba(120,200,120,0.9)", marginTop: 2 }}>
                              GPS {r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}
                            </div>
                          </>
                        ) : (
                          <>
                            <div>{geoLabelOf(u)}</div>
                            {u.timezone && (u.city || u.region) && (
                              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{u.timezone}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td style={td}>
                        {r.push ? (
                          <span style={{ background: "rgba(120,200,120,0.18)", color: "#9be29b", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999 }}>
                            ✓{r.platform ? ` ${r.platform}` : ""}
                          </span>
                        ) : (
                          <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtMiami(u.first_seen)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtMiami(u.last_seen)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{u.events}</td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </main>
  );
};

const Stat = ({ label, value, muted }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12,
      padding: "14px 18px",
      minWidth: 150,
    }}
  >
    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
    <div style={{ color: muted ? "rgba(255,255,255,0.7)" : "#fff", fontSize: muted ? 15 : 24, fontWeight: 700, marginTop: 4 }}>{value}</div>
  </div>
);

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = { padding: "12px 14px", fontSize: 13, verticalAlign: "middle" };

export default AnonymousUsers;
